import React from "react";
import { motion } from "framer-motion";
import {
  Search, FileText, PenLine, Banknote, BarChart3, DollarSign
} from "lucide-react";

const STEPS = [
  {
    icon: Search,
    title: "Review Opportunities",
    desc: "Browse deals vetted through our underwriting process. Each opportunity includes property details, borrower track records, financial projections, and risk assessments.",
    detail: "All deals are independently underwritten with strict LTV/LTC limits before being published to investors.",
    color: "text-blue-500",
    bg: "bg-blue-50 border-blue-200",
  },
  {
    icon: FileText,
    title: "Access Deal Room",
    desc: "Committed investors get full access to the deal room including legal documents, appraisals, scope of work, and comparable sales analysis.",
    detail: "Documents are secured with role-based visibility controls and audit logging.",
    color: "text-violet-500",
    bg: "bg-violet-50 border-violet-200",
  },
  {
    icon: PenLine,
    title: "Commit Capital",
    desc: "Once you've reviewed the opportunity, commit your investment amount. Only accredited investors can commit capital to ensure compliance.",
    detail: "Minimum investment amounts are set per deal. Your commitment is tracked in real-time.",
    color: "text-emerald-500",
    bg: "bg-emerald-50 border-emerald-200",
  },
  {
    icon: Banknote,
    title: "Fund & Close",
    desc: "When the raise target is met, capital is deployed to the deal. You'll receive confirmation and can track progress through your portfolio dashboard.",
    detail: "Syndication is automated. Capital deployment follows strict allocation rules.",
    color: "text-amber-500",
    bg: "bg-amber-50 border-amber-200",
  },
  {
    icon: BarChart3,
    title: "Track Progress",
    desc: "Monitor your active deals in real-time. See deal stage, capital deployed, and timeline updates throughout the loan lifecycle.",
    detail: "Your portfolio dashboard shows commitment status, deal stage, and projected returns.",
    color: "text-cyan-500",
    bg: "bg-cyan-50 border-cyan-200",
  },
  {
    icon: DollarSign,
    title: "Receive Distributions",
    desc: "As deals pay interest and principal returns, distributions flow to your capital account. Track every payment with full transparency.",
    detail: "Distribution history shows each payment by deal with date, amount, and type (interest vs principal).",
    color: "text-green-500",
    bg: "bg-green-50 border-green-200",
  },
];

export default function InvestmentProcessTab() {
  return (
    <div className="space-y-6" data-testid="investment-process-tab">
      <div className="text-center mb-6">
        <h2 className="text-lg font-bold text-[#0A0A0A] mb-1">How Investing Works</h2>
        <p className="text-sm text-[#888] max-w-lg mx-auto">
          A simple, structured process from opportunity review to receiving returns.
        </p>
      </div>

      <div className="space-y-4">
        {STEPS.map((step, i) => (
          <motion.div key={step.title}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}
            className="rounded-xl border border-[#e8e0d0] bg-white p-4 flex gap-4 hover:bg-[#faf7f2]/60 transition-colors"
            data-testid={`process-step-${i}`}>
            <div className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 ${step.bg}`}>
              <step.icon className={`h-5 w-5 ${step.color}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black text-[#ccc]">STEP {i + 1}</span>
                <h4 className="text-sm font-bold text-[#0A0A0A]">{step.title}</h4>
              </div>
              <p className="text-sm text-[#555] leading-relaxed">{step.desc}</p>
              <p className="text-[10px] text-[#999] mt-1.5 italic">{step.detail}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Platform Trust Signals */}
      <div className="rounded-xl border border-[#e8e0d0] bg-gradient-to-br from-[#faf7f2] to-white p-5 mt-6">
        <h4 className="text-[10px] uppercase tracking-wider text-[#999] font-semibold mb-3">Platform Governance</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Document Security", value: "Role-Based" },
            { label: "Audit Logging", value: "All Events" },
            { label: "Investor Isolation", value: "Peer-Protected" },
            { label: "Accreditation", value: "Verified Gate" },
          ].map(s => (
            <div key={s.label} className="text-center p-2.5 rounded-lg bg-white border border-[#e8e0d0]">
              <div className="text-xs font-bold text-emerald-600">{s.value}</div>
              <div className="text-[9px] text-[#999]">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
